import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import CircularProgress from 'components/shared/CircularProgress';
import { useProfile } from 'context/profile';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import { PERMISSIONS } from 'models/Profile';
import {
  AdminPortalProblemReport,
  adminPortalProblemReportQueryKeys,
  indexAdminPortalProblemReports,
  PROBLEM_REPORT_CATEGORY_LABELS,
  showAdminPortalProblemReport,
} from 'services/api/admin/portalProblemReports';
import { AdminPortalSystem, adminPortalSystemQueryKeys, showAdminPortalSystem } from 'services/api/admin/portalSystems';
import { colors } from 'styles/theme/colors';

import { downloadProblemReportBundle } from './downloadBundle';
import ProblemReportSlideout from './ProblemReportSlideout';

// Admin → Portal Systems → <portal> → Problem reports: what coordinators reported from the
// extension on this portal, across every client using it. Grouped by portal because a portal's own
// change (a deploy that moves a label) reaches every client at once, and a run of reports with the
// same new page layout reads as that one event.
export default function PortalProblemReports() {
  const { id = '' } = useParams<{ id: string }>();
  const { data: system } = useQuery({
    queryKey: adminPortalSystemQueryKeys.show(id),
    queryFn: ({ signal }) => showAdminPortalSystem(id, signal),
    enabled: !!id,
  });

  if (!system) return <CircularProgress />;

  return <ReportsList system={system} />;
}

function ReportsList({ system }: { system: AdminPortalSystem }) {
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminClientEdit);
  const basePath = `/admin/portal-systems/${system.id}/problem-reports`;

  const config = useMemo<AdminListPageConfig<AdminPortalProblemReport>>(
    () => ({
      title: `${system.displayName} · Problem Reports`,
      columnStorageKey: 'admin.portalProblemReports',
      queryKey: adminPortalProblemReportQueryKeys.index(system.systemName),
      fetchFn: indexAdminPortalProblemReports,
      fetchParams: { defaultSort: 'createdAt desc', pageSize: 25, extra: { sourceSystem: system.systemName } },
      showResultCount: true,
      headerExtra: <Link to='/admin/portal-systems'>All portal systems</Link>,
      filters: [
        {
          key: 'status',
          label: 'Include resolved',
          type: 'checkbox',
          toRequest: (includeResolved: boolean) => (includeResolved ? undefined : 'open'),
        },
      ],
      columns: [
        {
          id: 'createdAt',
          header: 'When',
          sortable: true,
          size: 170,
          render: (row) => simpleDateAtTime(row.createdAt),
        },
        { id: 'client', header: 'Client', sortable: false, render: (row) => row.client.name },
        { id: 'user', header: 'Reporter', sortable: false, render: (row) => row.user.email },
        {
          id: 'category',
          header: "What's wrong",
          sortable: false,
          render: (row) => (
            <>
              {PROBLEM_REPORT_CATEGORY_LABELS[row.category] ?? row.category}
              {row.resolvedAt && <Resolved>Resolved</Resolved>}
            </>
          ),
        },
        { id: 'fields', header: 'Fields', sortable: false, render: (row) => row.fields.join(', ') || '—' },
        {
          id: 'pageFingerprint',
          header: 'Page layout',
          sortable: false,
          render: (row) =>
            row.pageFingerprint ? (
              <>
                <Fingerprint>{row.pageFingerprint.slice(0, 6)}</Fingerprint>
                {row.newLayout && <NewLayout title='No earlier report on this portal had this layout'>new</NewLayout>}
              </>
            ) : (
              '—'
            ),
        },
        { id: 'extensionVersion', header: 'Version', sortable: false, render: (row) => row.extensionVersion ?? '—' },
      ],
      rowActions: [
        { label: 'View', onClick: (row, { openSlideout }) => openSlideout('report', row) },
        { label: 'Download debug bundle', onClick: (row) => downloadProblemReportBundle(row.id) },
      ],
      onRowClick: (row, { openSlideout }) => openSlideout('report', row),
      slideouts: {
        report: {
          render: (row, onClose) => <ProblemReportSlideout report={row} canEdit={canEdit} onClose={onClose} />,
          routeBasePath: basePath,
          fetchRow: (reportId) => showAdminPortalProblemReport(reportId),
        },
      },
    }),
    [system, canEdit, basePath]
  );

  const state = useAdminListPage(config);

  return <AdminListPageRenderer config={config} state={state} />;
}

const Fingerprint = styled.code`
  font-size: 12px;
  color: ${colors.black75};
`;

const NewLayout = styled.span`
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background: ${colors.brandYellow100};
  color: ${colors.brandNavy100};
`;

const Resolved = styled.span`
  margin-left: 6px;
  font-size: 12px;
  color: ${colors.black50};
`;
